/**
 * ConfirmSendModal — E-93 Story 93.12.
 *
 * Type-to-confirm dialog for large bulk email sends. Safety gate: above a
 * configurable recipient threshold the user must type the exact count into
 * the input before the "Send" button un-disables. Prevents accidental
 * mass-sends from a misclick.
 *
 * ## Adversarial picks
 *
 * - **Snapshot count, not live reference.** Parent passes `count` at the
 *   moment the modal opens; subsequent selection changes do NOT retarget
 *   the confirm value mid-flow.
 * - **Pure helper** `validateConfirmInput` is exported for jsdom-safe
 *   unit tests. We deliberately skip a full render test because the
 *   Radix+TanStack+jsdom stack has crashed in prior stories (95-9).
 * - **Leading zeros rejected** — `"0100" !== "100"` by design. If the
 *   count is 100 the user must type exactly "100".
 * - **Trim whitespace** so a stray space from the paste buffer does not
 *   block a legitimate confirm.
 */

import { Input } from '@/components/ui/input';
import { useTranslation } from '@kgc/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@kgc/ui';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';

export interface ConfirmSendModalProps {
  /** Controls open state; parent owns the boolean. */
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Snapshot count — must NOT be a live reference to a mutable counter. */
  count: number;
  onConfirm: () => void;
}

/**
 * Returns true when the user-typed input exactly matches the snapshot count.
 * Exported so tests can cover the equality semantics without rendering the
 * Radix Dialog tree.
 */
export function validateConfirmInput(input: string, count: number): boolean {
  const trimmed = input.trim();
  if (trimmed.length === 0) return false;
  if (!/^[1-9]\d*$/.test(trimmed)) return false;
  return trimmed === String(count);
}

export function ConfirmSendModal({
  open,
  onOpenChange,
  count,
  onConfirm,
}: ConfirmSendModalProps): JSX.Element {
  const { t } = useTranslation();
  const [input, setInput] = useState('');

  // Reset the typed value every time the modal reopens so a previous typo
  // from an abandoned confirm cannot sneak through on the next attempt.
  useEffect(() => {
    if (open) setInput('');
  }, [open]);

  const isValid = validateConfirmInput(input, count);

  function handleConfirm(): void {
    if (!isValid) return;
    onConfirm();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('email.bulkSend.confirmModal.title', { count })}</DialogTitle>
          <DialogDescription>{t('email.bulkSend.confirmModal.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <label htmlFor="confirm-send-count" className="text-sm font-medium">
            {t('email.bulkSend.confirmModal.inputLabel')}
          </label>
          <Input
            id="confirm-send-count"
            autoFocus
            inputMode="numeric"
            pattern="[0-9]*"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={t('email.bulkSend.confirmModal.inputPlaceholder')}
            aria-describedby="confirm-send-hint"
          />
          <p id="confirm-send-hint" className="text-xs text-muted-foreground">
            {t('email.bulkSend.confirmModal.hint', { count })}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('email.bulkSend.confirmModal.cancelButton')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!isValid}
            aria-disabled={!isValid}
            title={!isValid ? t('email.bulkSend.confirmModal.mismatchTooltip') : undefined}
          >
            {t('email.bulkSend.confirmModal.confirmButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
